import React, {
	useCallback,
	useState,
	useEffect,
	type KeyboardEvent,
	type ElementRef,
	memo,
	useImperativeHandle,
} from 'react';
import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Flex, xcss, Text } from '@atlaskit/primitives';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { PopupComponent } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { useDateTimeFormatter } from '@atlassian/jira-issue-format-date/src/main.tsx';
import { DEFAULT_ROW_HEIGHT, LIST_FIELD_POPUP_OFFSET } from '../../../common/constants.tsx';
import {
	EmptyCellContent,
	Container,
	CellContainer,
} from '../../../common/ui/list-cell/styled.tsx';
import DateTimeFieldEditView from './async-date-time-picker/index.tsx';
import messages from './messages.tsx';
import { CellContent } from './styled.tsx';
import type { DateTimeCellProps, DateTimeValue } from './types.tsx';

const POPUP_OFFSET: [number, number] = LIST_FIELD_POPUP_OFFSET;
const POPUP_OFFSET_NEW: [number, number] = [0, -DEFAULT_ROW_HEIGHT - 1];

const DateTimeCell = memo(
	({
		isEditable = false,
		issueKey = '',
		fieldKey,
		fieldValue = null,
		width,
		height,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		isHovering: isHoveringNew = false,
		cellRef,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		inputValue,
		onInputChange = noop,
	}: DateTimeCellProps) => {
		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();
		const [textInputValueOld, setTextInputValueOld] = useState<DateTimeValue>(fieldValue);

		const textInputValue = fg('jira_list_keyboard_nav')
			? (inputValue ?? fieldValue) || ''
			: textInputValueOld;

		const setTextInputValueNew = useCallback(
			(value: string | null) => onInputChange(value === null ? '' : value),
			[onInputChange],
		);

		const setTextInputValue = fg('jira_list_keyboard_nav')
			? setTextInputValueNew
			: setTextInputValueOld;

		const { formatDateTime } = useDateTimeFormatter(null);
		const [isHoveringOld, setIsHovering] = useState(false);
		const isHovering = fg('jira_list_keyboard_nav') ? isHoveringNew : isHoveringOld;
		const [{ value, error }, { saveValue, resetError }] = useEditField<string | null, null>({
			fieldKey,
			issueKey,
			fieldType: 'datetime',
			initialValue: fieldValue,
		});

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleMouseEnter in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleMouseEnter = useCallback(() => setIsHovering(true), [setIsHovering]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleMouseLeave in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleMouseLeave = useCallback(() => setIsHovering(false), [setIsHovering]);

		useEffect(() => {
			setTextInputValue(value);
		}, [setTextInputValue, value]);

		useEffect(() => {
			// will close the popup and blur cell if user clicks on another cell
			if (!fg('jira_list_keyboard_nav') && !isSelected && editMode) {
				setEditMode(false);
			}
		}, [editMode, isSelected, setEditMode]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleClick in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleClick = useCallback(() => {
			if (isEditable && !editMode) {
				setEditMode(true);
			}
		}, [editMode, isEditable, setEditMode]);

		const handleChange = useCallback(
			(newValue: string | null) => {
				resetError();
				if (newValue === value) {
					return;
				}
				saveValue(newValue, null, createAnalyticsEvent({}));
			},
			[createAnalyticsEvent, resetError, saveValue, value],
		);

		const saveAndClose = useCallback(() => {
			setEditMode(false);
			if (textInputValue === value) {
				return;
			}
			saveValue(textInputValue, null, createAnalyticsEvent({}));
		}, [createAnalyticsEvent, saveValue, textInputValue, value, setEditMode]);

		const staticContent = textInputValue !== null && textInputValue !== '' && (
			<Container>
				<CellContent>{formatDateTime(textInputValue)}</CellContent>
			</Container>
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated wrapperRef is now managed in CellNavigation
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleKeyDown in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const onEscapeKeyPress = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				setEditMode(false);
				wrapperRef?.current?.focus();
			},
			[wrapperRef, setEditMode],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleKeyDown in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleKeyDown = useCallback(
			(event: KeyboardEvent<HTMLDivElement>) => {
				if (event.key === 'Escape') {
					onEscapeKeyPress(event);
				} else {
					!editMode && onKeyDown?.(event);
				}
			},
			[editMode, onEscapeKeyPress, onKeyDown],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleKeyDown in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleContentKeyDown = useCallback(
			(event: KeyboardEvent<Element>) => {
				if (event.key === 'Escape') {
					event.preventDefault();
					wrapperRef?.current?.focus();
				}
			},
			[wrapperRef],
		);

		const handleKeyDownNew = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && ['Enter', 'Tab'].includes(event.code)) {
					return true;
				}
				return false;
			},
			[editMode],
		);

		const popupContent = useCallback(
			() => (
				<DateTimeFieldEditView
					width={width}
					height={height}
					value={textInputValue}
					onChange={handleChange}
					onKeyDown={fg('jira_list_keyboard_nav') ? noop : handleContentKeyDown}
				/>
			),
			[handleChange, handleContentKeyDown, height, textInputValue, width],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<div {...triggerProps}>
					<Container>
						{textInputValue !== null && textInputValue !== '' && (
							<CellContent>{formatDateTime(textInputValue)}</CellContent>
						)}
						{(textInputValue === null || textInputValue === '') && (
							<EmptyCellContent>
								<RecentIcon label={formatMessage(messages.emptyDate)} />
								<Text>{formatMessage(messages.emptyDate)}</Text>
							</EmptyCellContent>
						)}
					</Container>
				</div>
			),
			[formatDateTime, formatMessage, textInputValue],
		);

		useImperativeHandle(cellRef, () => ({ onKeyDown: handleKeyDownNew }), [handleKeyDownNew]);

		const cellContent = (
			<>
				{isHovering || editMode || isSelected ? (
					<Popup
						autoFocus={false}
						isOpen={editMode}
						placement="bottom-start"
						offset={fg('jira_list_keyboard_nav') ? POPUP_OFFSET_NEW : POPUP_OFFSET}
						popupComponent={PopupComponent}
						onClose={saveAndClose}
						content={popupContent}
						trigger={popupTrigger}
					/>
				) : (
					staticContent
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<Flex
					xcss={[wrapperStyles]}
					testId="business-list.ui.list-view.date-time-cell.cell-container"
				>
					{cellContent}
				</Flex>
			);
		}

		return (
			<CellContainer
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleClick}
				isFocused={isSelected}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				data-testid="business-list.ui.list-view.date-time-cell.cell-container"
				ref={onWrapperRef}
			>
				{cellContent}
			</CellContainer>
		);
	},
);

export default DateTimeCell;

const wrapperStyles = xcss({
	width: '100%',
	height: '100%',
});
